import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  requireRoles?: Array<"admin" | "auctioneer" | "clerk" | "bidder">;
  redirectTo?: string;
}

/**
 * ProtectedRoute component for consistent authentication and authorization
 *
 * @param requireAuth - Whether authentication is required (default: true)
 * @param requireRoles - Array of roles required to access this route
 * @param redirectTo - Where to redirect if access is denied (default: "/auth")
 */
export const ProtectedRoute = ({
  children,
  requireAuth = true,
  requireRoles = [],
  redirectTo = "/auth",
}: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { isAtLeast, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || roleLoading) {
      return;
    }

    if (requireAuth && !user) {
      navigate(redirectTo);
      return;
    }

    if (requireRoles.length > 0) {
      // Role hierarchy: admin ≥ auctioneer ≥ clerk ≥ bidder.
      // Any role in requireRoles grants access if the user is at or above that rank.
      const hasRequiredRole = requireRoles.some(role => isAtLeast(role));

      if (!hasRequiredRole) {
        navigate("/");
        return;
      }
    }
  }, [
    user,
    authLoading,
    roleLoading,
    isAtLeast,
    requireAuth,
    requireRoles,
    navigate,
    redirectTo,
  ]);

  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <>{children}</>;
};
